if (!customElements.get('bg-product-visual-gallery')) {
  customElements.define(
    'bg-product-visual-gallery',
    class BgProductVisualGallery extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-bg-visual-viewport]');
        this.slides = Array.from(this.querySelectorAll('[data-bg-visual-slide]'));
        this.previousButton = this.querySelector('[data-bg-visual-prev]');
        this.nextButton = this.querySelector('[data-bg-visual-next]');
        this.modal = this.querySelector('[data-bg-visual-modal]');
        this.modalImage = this.querySelector('[data-bg-visual-modal-image]');
        this.modalPreviousButton = this.querySelector('[data-bg-visual-modal-prev]');
        this.modalNextButton = this.querySelector('[data-bg-visual-modal-next]');
        this.modalCurrentLabel = this.querySelector('[data-bg-visual-modal-current]');
        this.visualLinks = Array.from(this.querySelectorAll('[data-bg-visual-open]'));
        this.currentIndex = 0;
        this.modalIndex = 0;

        if (!this.viewport || this.slides.length === 0) return;

        this.previousButton?.addEventListener('click', () => this.movePage(-1));
        this.nextButton?.addEventListener('click', () => this.movePage(1));

        this.viewport.addEventListener('scroll', () => this.requestUpdate(), { passive: true });
        this.viewport.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.movePage(-1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.movePage(1);
          }
        });

        this.visualLinks.forEach((link, index) => {
          link.addEventListener('click', (event) => this.openModal(event, index));
        });
        this.modalPreviousButton?.addEventListener('click', () => this.showModalImage(this.modalIndex - 1));
        this.modalNextButton?.addEventListener('click', () => this.showModalImage(this.modalIndex + 1));
        this.querySelector('[data-bg-visual-close]')?.addEventListener('click', () => this.modal?.close());
        this.modal?.addEventListener('click', (event) => {
          if (event.target === this.modal) this.modal.close();
        });
        this.modal?.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.showModalImage(this.modalIndex - 1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.showModalImage(this.modalIndex + 1);
          }
        });

        this.updateState(0);
      }

      requestUpdate() {
        if (this.updateFrame) return;
        this.updateFrame = requestAnimationFrame(() => {
          this.updateFrame = null;
          this.updateState(this.getClosestSlideIndex(this.viewport.scrollLeft));
        });
      }

      showSlide(index) {
        const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
        const requestedIndex = Math.max(0, Math.min(index, this.slides.length - 1));
        const targetLeft = Math.min(this.slides[requestedIndex].offsetLeft, maxScroll);
        let resolvedIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.slides.forEach((slide, slideIndex) => {
          const distance = Math.abs(targetLeft - slide.offsetLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            resolvedIndex = slideIndex;
          }
        });

        this.viewport.scrollTo({ left: targetLeft, behavior: 'smooth' });
        this.updateState(resolvedIndex);
      }

      movePage(direction) {
        const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
        const pageStep = this.getPageStep();
        if (pageStep <= 0) return;

        const currentPage = Math.round(this.viewport.scrollLeft / pageStep);
        const targetPage = Math.max(0, currentPage + direction);
        const targetLeft = Math.min(targetPage * pageStep, maxScroll);

        this.viewport.scrollTo({ left: targetLeft, behavior: 'smooth' });
        this.updateState(this.getClosestSlideIndex(targetLeft));
      }

      getPageStep() {
        if (this.slides.length < 2) return this.viewport.clientWidth;

        const itemStep = this.slides[1].offsetLeft - this.slides[0].offsetLeft;
        if (itemStep <= 0) return this.viewport.clientWidth;

        const itemWidth = this.slides[0].getBoundingClientRect().width;
        const gap = Math.max(0, itemStep - itemWidth);
        const visibleCount = Math.max(1, Math.round((this.viewport.clientWidth + gap) / itemStep));

        return itemStep * visibleCount;
      }

      getClosestSlideIndex(scrollLeft = this.viewport.scrollLeft) {
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.slides.forEach((slide, index) => {
          const slideLeft = slide.offsetLeft - this.slides[0].offsetLeft;
          const distance = Math.abs(scrollLeft - slideLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      updateState(index) {
        this.currentIndex = index;
        const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
        if (this.previousButton) this.previousButton.disabled = this.viewport.scrollLeft <= 2;
        if (this.nextButton) this.nextButton.disabled = this.viewport.scrollLeft >= maxScroll - 2;
      }

      openModal(event, index) {
        if (!this.modal || !this.modalImage || typeof this.modal.showModal !== 'function') return;
        event.preventDefault();
        this.showModalImage(index);
        this.modal.showModal();
      }

      showModalImage(index) {
        if (!this.modalImage || this.visualLinks.length === 0) return;
        const nextIndex = Math.max(0, Math.min(index, this.visualLinks.length - 1));
        const link = this.visualLinks[nextIndex];

        this.modalIndex = nextIndex;
        this.modalImage.src = link.href;
        this.modalImage.alt = link.dataset.bgVisualAlt || '';
        if (this.modalCurrentLabel) this.modalCurrentLabel.textContent = String(nextIndex + 1);
        if (this.modalPreviousButton) this.modalPreviousButton.disabled = nextIndex === 0;
        if (this.modalNextButton) this.modalNextButton.disabled = nextIndex === this.visualLinks.length - 1;
      }
    }
  );
}
